const MIN_SEARCH_LENGTH = 3

class PredictiveSearch extends window.BAO.CustomElement() {
  constructor () {
    super()

    /** @type {AbortController | null} */
    this.searchAbortController = null
    /** @type {AbortController | null} */
    this.countAbortController = null
  }

  connectedCallback () {
    super.connectedCallback()

    const searchTerm = this.els.input.element.value.trim()

    if (searchTerm.length >= MIN_SEARCH_LENGTH) {
      this.showResults()
      this.getSearchResults(searchTerm)
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onInputChange = this.onInputChange.bind(this)
    this.handleDrawerTrigger = this.handleDrawerTrigger.bind(this)

    this.listeners.add(
      this.els.input.element,
      'input',
      window.BAO.Utils.debounce((e) => {
        this.onInputChange(e)
      }, 500),
    )

    this.listeners.add(this, 'click', this.handleViewAllClick.bind(this))

    this.externalEls.trigger.elements.forEach((triggerEl) => {
      this.listeners.add(triggerEl, 'click', this.handleDrawerTrigger)
    })
  }

  get productsLinkEl () {
    return (
      this.els['products-link']?.element ||
      this.querySelector('[data-predictive-search-el="products-link"]')
    )
  }

  getSearchResultsUrl (searchTerm) {
    return `${window.Shopify.routes.root}search?q=${encodeURIComponent(
      searchTerm,
    )}&type=product`
  }

  handleViewAllClick (event) {
    const link = event.target.closest('[data-predictive-search-el="products-link"]')
    if (!link) return

    const searchTerm = this.els.input.element.value.trim()
    if (!searchTerm) {
      event.preventDefault()
      return
    }

    link.href = this.getSearchResultsUrl(searchTerm)
  }

  onInputChange () {
    const searchTerm = this.els.input.element.value.trim()

    if (searchTerm.length >= MIN_SEARCH_LENGTH) {
      this.showResults()
      this.getSearchResults(searchTerm)
    } else {
      this.abortPendingRequests()
      this.showDefault()
    }
  }

  abortPendingRequests () {
    this.searchAbortController?.abort()
    this.countAbortController?.abort()
    this.searchAbortController = null
    this.countAbortController = null
  }

  /**
   * @param {string} searchTerm
   *
   * @returns {URLSearchParams}
   */
  buildSuggestParams (searchTerm) {
    const params = new URLSearchParams()

    params.set('q', searchTerm)
    params.set('resources[type]', 'query,product,collection,page')
    params.set('resources[limit]', '6')
    params.set('resources[limit_scope]', 'each')
    params.set(
      'resources[options][fields]',
      'body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor',
    )

    return params
  }

  get searchLinksEl () {
    return this.querySelector('[data-predictive-search-el="search-links"]')
  }

  get defaultLinksEl () {
    return this.querySelector('[data-predictive-search-el="default-links"]')
  }

  /**
   * @param {{ hasSearchLinks: boolean }} state
   */
  setLinksColumnState ({ hasSearchLinks }) {
    const searchLinksEl = this.searchLinksEl
    const defaultLinksEl = this.defaultLinksEl

    if (!searchLinksEl || !defaultLinksEl) return

    searchLinksEl.hidden = hasSearchLinks !== true
    defaultLinksEl.hidden = hasSearchLinks === true
  }

  getSearchResults (searchTerm) {
    this.abortPendingRequests()

    this.searchAbortController = new AbortController()
    this.countAbortController = new AbortController()

    const searchSignal = this.searchAbortController.signal
    const countSignal = this.countAbortController.signal
    const suggestParams = this.buildSuggestParams(searchTerm)
    const sectionParams = new URLSearchParams(suggestParams)

    sectionParams.set('section_id', 'predictive-search')

    fetch(
      `${window.Shopify.routes.root}search/suggest.json?${suggestParams.toString()}`,
      { signal: searchSignal },
    )
      .then((response) => {
        if (searchSignal.aborted) return

        if (!response.ok) throw new Error(response.statusText)

        return response.json()
      })
      .then((json) => {
        if (searchSignal.aborted || !json) return

        this.renderLinksFromJson(json)
      })
      .catch((e) => {
        if (e.name === 'AbortError') return

        console.error(e)
      })

    fetch(
      `${window.Shopify.routes.root}search/suggest?${sectionParams.toString()}`,
      { signal: searchSignal },
    )
      .then((response) => {
        if (searchSignal.aborted) return

        if (!response.ok) throw new Error(response.statusText)

        return response.text()
      })
      .then((text) => {
        if (searchSignal.aborted || !text) return

        const html = new DOMParser().parseFromString(text, 'text/html')
        const productResults =
          html.querySelector('#predictive-search-products') ??
          html.querySelector(
            '[id^="shopify-section"] #predictive-search-products',
          )

        this.handleProductResults(productResults, searchTerm)
      })
      .catch((e) => {
        if (e.name === 'AbortError') return

        console.error(e)
      })

    fetch(
      `${window.Shopify.routes.root}search?q=${encodeURIComponent(searchTerm)}&type=product&view=count&sections=section-search-count`,
      { signal: countSignal },
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText)

        return response.json()
      })
      .then((res) => {
        if (countSignal.aborted) return

        const newDOM = window.BAO.sectionRenderer.convertToDOM(
          res['section-search-count'],
        )
        const myself = newDOM.querySelector('.shopify-section')

        if (this.els['products-count']?.exists) {
          this.els['products-count'].element.textContent = myself.innerText
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError') return
      })
  }

  showDefault () {
    this.els.products.element.setAttribute('aria-hidden', 'true')
    this.els.default.element.setAttribute('aria-hidden', 'false')
    this.clearLinksColumn()
  }

  showResults () {
    this.els.products.element.setAttribute('aria-hidden', 'false')
    this.els.default.element.setAttribute('aria-hidden', 'true')
    this.setLinksColumnState({ hasSearchLinks: false })
  }

  clearLinksColumn () {
    const searchLinksEl = this.searchLinksEl

    if (!searchLinksEl) return

    searchLinksEl.innerHTML = ''
    searchLinksEl.dataset.resultCount = '0'
    searchLinksEl.hidden = true
    this.setLinksColumnState({ hasSearchLinks: false })
  }

  /**
   * @param {Object} json
   */
  renderLinksFromJson (json) {
    const searchLinksEl = this.searchLinksEl

    if (!searchLinksEl) return

    const results = json?.resources?.results ?? {}
    const parts = []
    let count = 0

    const isMobileSearchLayout = window.BAO.MediaBreakpoints.isMd !== true
    const queryLimit = isMobileSearchLayout === true ? 3 : 6
    const queries = results.queries?.slice(0, queryLimit) || []

    if (queries.length) {
      parts.push(
        this.renderLinkGroup(this.queryResultsLabel, queries, {
          useStyledText: true,
        }),
      )
      count += queries.length
    }

    if (isMobileSearchLayout !== true && results.collections?.length) {
      parts.push(
        this.renderLinkGroup(this.collectionResultsLabel, results.collections),
      )
      count += results.collections.length
    }

    if (isMobileSearchLayout !== true && results.pages?.length) {
      parts.push(
        this.renderLinkGroup(this.pageResultsLabel, results.pages),
      )
      count += results.pages.length
    }

    searchLinksEl.innerHTML = parts.join('')
    searchLinksEl.dataset.resultCount = count.toString()
    this.setLinksColumnState({ hasSearchLinks: count > 0 })
  }

  get queryResultsLabel () {
    return this.dataset.queryResultsLabel || 'Suggestions'
  }

  get collectionResultsLabel () {
    return this.dataset.collectionResultsLabel || 'Collections'
  }

  get pageResultsLabel () {
    return this.dataset.pageResultsLabel || 'Pages'
  }

  /**
   * @param {string} heading
   * @param {Object[]} items
   * @param {{ useStyledText?: boolean }} [options]
   *
   * @returns {string}
   */
  renderLinkGroup (heading, items, { useStyledText = false } = {}) {
    const itemsHtml = items
      .map((item) => {
        const label = useStyledText
          ? item.styled_text || this.escapeHtml(item.text)
          : this.escapeHtml(item.title || item.text)

        return `<li class="drw-SearchButtons_ButtonItem"><a href="${this.escapeAttr(item.url)}" class="drw-SearchButtons_Button fz-14_15">${label}</a></li>`
      })
      .join('')

    return `<div class="drw-SearchButtons"><h2 class="drw-SearchButtons_Heading fz-16_18">${this.escapeHtml(heading)}</h2><ul class="drw-SearchButtons_Buttons">${itemsHtml}</ul></div>`
  }

  /**
   * @param {string} value
   *
   * @returns {string}
   */
  escapeHtml (value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  /**
   * @param {string} value
   *
   * @returns {string}
   */
  escapeAttr (value) {
    return this.escapeHtml(value).replace(/'/g, '&#39;')
  }

  handleProductResults (results, searchTerm) {
    if (!results) return

    this.els['products-grid'].element.innerHTML = results.innerHTML
    this.reinitSwymButtons()

    const productsLink = this.productsLinkEl
    if (productsLink) {
      productsLink.setAttribute(
        'aria-hidden',
        results.dataset.resultCount === '0' ? 'true' : 'false',
      )
      productsLink.href = this.getSearchResultsUrl(searchTerm)
    }

    if (this.els['showing-products']?.exists) {
      this.els['showing-products'].element.innerText =
        results.dataset.resultCount
    }
  }

  reinitSwymButtons () {
    if (!window._swat?.initializeActionButtons) return

    window._swat.initializeActionButtons(this.els['products-grid'].element)
  }

  handleDrawerTrigger () {
    const tempInputEl = document.createElement('input')
    tempInputEl.style.position = 'absolute'
    tempInputEl.style.top = `${this.els.input.element.offsetTop + 7}px`
    tempInputEl.style.left = `${this.els.input.element.offsetLeft}px`
    tempInputEl.style.height = 0
    tempInputEl.style.opacity = 0
    document.body.appendChild(tempInputEl)
    tempInputEl.focus()

    window.setTimeout(() => {
      this.els.input.element.focus()
      this.els.input.element.click()
      document.body.removeChild(tempInputEl)
    }, 500)

    const searchTerm = this.els.input.element.value.trim()

    if (searchTerm.length >= MIN_SEARCH_LENGTH) {
      this.showResults()
      this.getSearchResults(searchTerm)
    } else {
      this.abortPendingRequests()
      this.showDefault()
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch)
}
